// noinspection HttpUrlsUsage

import * as React from "react";
import {
  Button,
  Checkbox,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Container} from "@material-ui/core";
import {ColorRamp, DEFAULT_STRETCH_PARAMETERS, IchConfig, IchParameter} from "./model";
import {ApolloApi} from "../api/ApolloApi";
import {Spinner} from "../common/ui/spinner/Spinner";
import Select from "react-select";

const LABEL_COL = 4;
const CONTROL_COL = 7;
const HELP_COL = 1;
const HIDDEN_PARAMETERS = ["Color Table", "Context"]
const ICH_PAGE_MESSAGES = defineMessages({
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },
  NAN: {
    id: "studio.style.ich.nan",
    defaultMessage: "Number value expected",
  },
  UNSIGNED: {
    id: "studio.style.ich.unsigned",
    defaultMessage: "Unsigned value expected",
  },
  FILE_EXPECTED: {
    id: "studio.style.ich.file-expected",
    defaultMessage: "File expected",
  },
  MIN: {
    id: "studio.style.ich.min-value",
    defaultMessage: "Minimum value",
  },
  MAX: {
    id: "studio.style.ich.max-value",
    defaultMessage: "Maximum value",
  },
  BAND_ERROR: {
    id: "studio.style.ich.bands.error",
    defaultMessage: "All bands must have a value or All bands must be empty."
  },
  SCALE_ERROR: {
    id: "studio.style.ich.scale.error",
    defaultMessage: "Empty or Positive value required."
  },
  SCALE_MIN_OVER_MAX_ERROR: {
    id: "studio.style.ich.scale-min-over-max.error",
    defaultMessage: "Minimum must be empty or less than Maximum."
  },
  SCALE_MAX_OVER_MIN_ERROR: {
    id: "studio.style.ich.scale-max-over-min.error",
    defaultMessage: "Maximum must be empty or greater than Minimum."
  },
  SCALE_MIN_EQUAL_MAX_ERROR: {
    id: "studio.style.ich.scale-min-equal-max.error",
    defaultMessage: "Minimum must not equal Maximum."
  },
  SCALE_MIN_LABEL: {
    id: "studio.style.ich.scale.min",
    defaultMessage: "From"
  },
  SCALE_MAX_LABEL: {
    id: "studio.style.ich.scale.max",
    defaultMessage: "To"
  },
  HELP_PROVIDERS: {
    id: "studio.style.ich.providers.help",
    defaultMessage: "Select an Image Chain Provider from the list."
  },
  HELP_BAND_SELECT: {
    id: "studio.style.ich.bands.help",
    defaultMessage: "Band selection: Leave empty to automatically apply a default band order based on the input dataset. Specifying band index values will override the order. Indexed from 1."
  },
  HELP_SCALE: {
    id: "studio.style.ich.scale.help",
    defaultMessage: "Minimum and Maximum scale for style."
  },
  HELP_TITLE: {
    id: "studio.style.ich.title.help",
    defaultMessage: "The Title entered here will appear in the Styles list."
  },
  HELP_ABSTRACT: {
    id: "studio.style.ich.abstract.help",
    defaultMessage: "Enter any relevant text describing the style."
  },
  HELP_KEYWORDS: {
    id: "studio.style.ich.keywords.help",
    defaultMessage: "Enter a comma separated list of keywords for style. Example: “keyword1, keyword2, keyword3”"
  },
  NO_FILE: {
    id: "studio.style.ich.no-file",
    defaultMessage: "No file selected"
  },
  ALT_COLOR_TABLE: {
    id: "studio.style.ich.ColorTableFilename.alt-image",
    defaultMessage: "Color Table sample"
  }
});

export interface IchStyleEditorPopupOwnProps {
  onClose: () => void;
  sld?: string;
  keywords?: string;
}

type IchStyleEditorPopupProps = IchStyleEditorPopupOwnProps & InjectedIntlProps;

interface IchStyleEditorPopupState {
  selectedIch: string;
  changedParameters: IchParameter[];
  title: string;
  keywords: string;
  abstract: string;
  scaleMax: string;
  scaleMin: string;
  scaleMaxError: string;
  scaleMinError: string;
  showConfirmationDialog: boolean;
  confirmationMessage: string;
  loading: boolean;
  errorProvider: boolean;
  errorColorRamp: boolean;
  ichConfig: IchConfig;
  colorRamps: ColorRamp[];
}

class IchStyleEditorPopupComponent extends React.Component<IchStyleEditorPopupProps, IchStyleEditorPopupState> {

  constructor(props: IchStyleEditorPopupProps) {
    super(props);

    this.state = {
      selectedIch: null,
      changedParameters: [],
      title: "",
      keywords: "",
      abstract: "",
      scaleMax: "",
      scaleMin: "",
      scaleMaxError: "",
      scaleMinError: "",
      showConfirmationDialog: false,
      confirmationMessage: "",
      errorProvider: false,
      errorColorRamp: false,
      loading: true,
      ichConfig: null,
      colorRamps: null
    };
  }

  componentDidMount() {
    Promise.allSettled([
      ApolloApi.getImageChainProviders(),
      ApolloApi.getImageChainColorRamps()
    ]).then((response) => {
      let ichConfig = null;
      let selectedIch = null;
      let colorRamps = null
      if (response[0].status === 'fulfilled') {
        const ichConfigResponse = response[0] as PromiseFulfilledResult<any>;
        ichConfig = this.hideParameters(ichConfigResponse.value);
        selectedIch = ichConfig.imageChainProviders.providers[0].imageChains[0].name;
      } else {
        this.setState({errorProvider: true});
      }
      if (response[1].status === 'fulfilled') {
        const colorRampsResponse = response[1] as PromiseFulfilledResult<any>;
        colorRamps = colorRampsResponse.value;
      } else {
        this.setState({errorColorRamp: true});
      }
      this.setState({
        loading: false,
        ichConfig, selectedIch, colorRamps
      });
      this.onLoad();

    })
  }

  hideParameters = (ichConfig) => {
    ichConfig.imageChainProviders.providers.forEach(provider => {
      provider.imageChains.forEach(imageChain => {
        HIDDEN_PARAMETERS.forEach(hiddenParameter => {
          const index = imageChain.parameters.findIndex(param => {
            return param.name === hiddenParameter
          });
          if (index) {
            imageChain.parameters.splice(index, 1);
          }
        })
      })
    })
    return ichConfig;
  }

  render() {
    const {onClose} = this.props;
    const {loading, showConfirmationDialog, confirmationMessage, errorProvider, errorColorRamp} = this.state;
    const error = errorProvider || errorColorRamp;

    return (
        <div>
          <Modal show={showConfirmationDialog} onHide={this.closeConfirmationDialog}>
            <Modal.Body>
              {confirmationMessage}
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={onClose}>
                <FormattedMessage id="studio.form.close" defaultMessage="Close"/>
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal className="ichStyleEditorPopup" show={true} onHide={onClose} backdrop={"static"}
                 keyboard={false}>
            <Modal.Header closeButton={true}>
              <Modal.Title>
                <FormattedMessage id="studio.style.ich.title" defaultMessage="Image Chain Style Editor"/>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="ichStyleEditorBodyContent">
                {error && <div>
                  {errorProvider && <FormattedMessage id="studio.style.ich.error.bad-provider-config"
                                                      defaultMessage={"Invalid Image Chain configuration on {url}"}
                                                      values={{url: `${document.location.origin}/apollo/api/styles/imagine/describe/providers`}}
                  />}
                  {errorColorRamp && <FormattedMessage id="studio.style.ich.error.bad-color-ramp-config"
                                                       defaultMessage={"Invalid Color Ramp configuration on {url}"}
                                                       values={{url: `${document.location.origin}/apollo/api/styles/imagine/describe/colorramps`}}
                  />}

                  <br/>
                  <FormattedMessage id="studio.style.ich.error.bad-config-admin"
                                    defaultMessage="Contact your administrator."
                  />
                </div>
                }
                {!error && loading &&
                 <div style={{textAlign: "center"}}>
                   <h2>
                     <FormattedMessage id="studio.settings.general.loading"
                                       defaultMessage="Loading..."/>
                   </h2>
                   <Spinner bigStyle/>
                 </div>
                }
                {!error && !loading && this.renderContent()}
              </div>
            </Modal.Body>
            <Modal.Footer>
              {!loading && this.renderButtons()}
            </Modal.Footer>
          </Modal>
        </div>
    );
  }

  private renderContent() {
    return (
        <Container>
          <FormGroup>
            {this.renderTitleAbstractKeywordsSection()}
            {this.renderScale()}
            {this.renderProviders()}
          </FormGroup>
          <Row>
            <hr/>
          </Row>
          {this.renderParameters()}
        </Container>);
  }

  private getStretchParameters = (provider, parameter, value) => {
    const {intl} = this.props;
    const stretchParameters: JSX.Element[] = [];
    const stretch = provider.parameters.find(param => (param.name === "Stretch Name"));
    const StretchName = this.getValue(stretch);
    if (StretchName) {
      const defaultStretchParam = DEFAULT_STRETCH_PARAMETERS.findIndex(
          stretchParamIndex => (stretchParamIndex.name.replace(/\s/g, '') === StretchName.value));
      if (defaultStretchParam >= 0 && DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters &&
          DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters.length > 0) {
        const numbers = value.value.substring(value.value.indexOf(':') + 1, value.value.length - 2).replace(/\s/g, '');
        const params = numbers.split(',');
        for (let i = 0; i < DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters.length; i++) {
          const newParameter = this.renderParameter(
              {
                name: DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters[i].name + "_SP",
                dataTypes: [{name: "IMAGINE.Double", attributes: []}],
                description: DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters[i].description
                             ? DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].parameters[i].description : null,
              }, {
                value: params[i],
                error: params[i].match(/^-?\d+\.?(\d+)?$/) ? "" : intl.formatMessage(ICH_PAGE_MESSAGES.NAN)
              });
          stretchParameters.push(newParameter);
        }
      }
    }

    return <div key={`key_${parameter.name}`}>{stretchParameters}</div>;
  }

  private renderParameter = (parameter, value) => {
    const {intl} = this.props;

    //uncomment the console.log line to get a list of the dynamic parameter help strings to add to en.json
    //unfortunately the manageTranslation.js script will delete these, so we will need to restore them before check-in
    //console.log(`"studio.style.ich.parameter.${parameter.name.replace(/\s/g, '')}.help" : "${parameter.description}",`);
    const isSP = parameter.name.endsWith("_SP");
    const parameterName = parameter.name.replace(/_SP$/, '');
    const min = parameter.dataTypes[0]?.attributes.find((param) => (param.type.toLowerCase() === "min"))?.value || null;
    const max = parameter.dataTypes[0]?.attributes.find((param) => (param.type.toLowerCase() === "max"))?.value || null;

    return <div key={`key_${parameter.name}`}>
      <Row className={"ichStyleEditorPopup-Row"} id={`row_${parameter.name.replace(/\s/g, '-')}`}>
        <Col componentClass={ControlLabel}
             sm={LABEL_COL}
             className={isSP ? "ichStyleEditorPopup-Label-SP" : "ichStyleEditorPopup-Label"}>
          <div>
            {/* tslint:disable */}
            {/* Disabled because: Variables are not allowed for translation messages */}
            <FormattedMessage id={`studio.style.ich.parameter.${parameterName.replace(/\s/g, '')}.label`}
                              defaultMessage={`${parameterName}`}/>
            {/* tslint:enable */}
            <span hidden={value.error === ""} style={{color: "red"}}>
                            <FormattedMessage id="studio.style.ich.error" defaultMessage="*"/>
                        </span>
          </div>
        </Col>
        <Col sm={CONTROL_COL}>
          {this.renderControl(parameter, value)}
        </Col>
        {parameter.description && <Col sm={HELP_COL} className={"ichStyleEditorPopup-Label"}>
          <OverlayTrigger placement="top" overlay={<Tooltip
              id="tooltip_help">
            {/* tslint:disable */}
            {/* Disabled because: Variables are not allowed for translation messages */}
            <FormattedMessage id={`studio.style.ich.parameter.${parameterName.replace(/\s/g, '')}.help`}
                              defaultMessage={`${parameter.description}`}/>
            {/* tslint:enable */}
            {(min || max) && <br/>}
            {min && <div><FormattedMessage
                id="studio.style.ich.min-help-value"
                defaultMessage="Minimum: {min}"
                values={{min}}
            />&nbsp;</div>}
            {max && <FormattedMessage
                id="studio.style.ich.max-help-value"
                defaultMessage="Maximum: {max}"
                values={{max}}
            />}
          </Tooltip>}>
            <img src={`theme/assets/images/help_16.png`} id={`${parameterName}`}
                 alt={intl.formatMessage(ICH_PAGE_MESSAGES.HELP)}/>
          </OverlayTrigger>
        </Col>
        }
      </Row>
    </div>
  }

  private renderParameters = () => {
    const {selectedIch, ichConfig} = this.state;
    const parameters: JSX.Element[] = [];
    let errors = false;
    const validParameterTypes = ["IMAGINE.String", "IMAGINE.Table", "IMAGINE.File", "IMAGINE.Bool",
                                 "IMAGINE.Int", "IMAGINE.Double", "IMAGINE.Unsigned", "IMAGINE.Scalar"];
    const provider = ichConfig.imageChainProviders.providers[0].imageChains.find(prov => (prov.name === selectedIch));
    if (!provider) {
      return;
    }

    let bandsRendered = false;
    provider.parameters.forEach((parameter) => {
      if (!bandsRendered && parameter.name > "Bands") {
        bandsRendered = true;
        parameters.push(this.renderBands(provider));
      }
      if (validParameterTypes.includes(parameter.dataTypes[0].name)) {
        const value = this.getValue(parameter);
        errors = errors || (value.error !== "");

        //uncomment the console.log line to get a list of the dynamic parameter labels to add to en.json
        //unfortunately the manageTranslation.js script will delete these, so we will need to restore them before check-in
        //console.log (`"studio.style.ich.parameter.${parameter.name.replace(/\s/g, '')}.label":"${parameter.name}",`);
        let element: JSX.Element;
        if (parameter.name === "Stretch Parameters") {
          element = this.getStretchParameters(provider, parameter, value);
        } else {
          element = this.renderParameter(parameter, value);
        }
        parameters.push(element);
      }
    });

    return parameters;
  }

  private getBandControl = (bandName, min, max) => {
    const {selectedIch} = this.state;
    const props = {
      type: "number",
      key: `controlKey_${selectedIch}_band_${bandName}`,
      name: `${selectedIch}_band_${bandName}`,
      onChange: this.onChange,
      alt: `${bandName} Band`,
      className: "form-control-band",
      min,
      max
    };
    const changedParameter = this.getParameterByName(`${selectedIch}_band_${bandName}`);

    props["value"] = (changedParameter) ? changedParameter.value : "";
    return (React.createElement("input", props));
  }

  private scalesValid = () => {
    const {scaleMinError, scaleMaxError} = this.state;
    return (scaleMinError === "" && scaleMaxError === "");
  }

  private scaleValid = (scale) => {
    const {scaleMinError, scaleMaxError} = this.state;
    if (scale === "min") {
      return (scaleMinError === "");
    } else {
      return (scaleMaxError === "");
    }
  }

  private bandsValid = () => {
    const {selectedIch, changedParameters} = this.state;
    return changedParameters.filter(parm => (parm.selectedIch === selectedIch &&
                                             parm.name.indexOf("_band_") > 0 &&
                                             parm.error !== "")).length === 0;
  }

  private renderBandControl = (band) => {
    if (this.bandsValid()) {
      return <div style={{float: "left", width: "20%"}}>
        <div className={`ich_box ich_${band.toLowerCase()}`}></div>
        &nbsp;{this.getBandControl(band, 1, 20)}
      </div>
    } else {
      return <OverlayTrigger placement="top"
                             overlay={<Tooltip id="tooltip_error">
                               <FormattedMessage id="studio.style.ich.bands.error"
                                                 defaultMessage="All bands must have a value or All bands must be empty."/>
                             </Tooltip>}>
        <div className={`ich_box ich_${band.toLowerCase()}`} style={{float: "left", width: "25%"}}>
          {this.getBandControl(band, 1, 20)}
        </div>
      </OverlayTrigger>
    }
  }

  private renderScaleControl = (scale) => {
    const {scaleMin, scaleMax, scaleMinError, scaleMaxError} = this.state;
    const {intl} = this.props;
    const scaleLower = scale.toLowerCase();
    const value = scaleLower === "min" ? scaleMin : scaleMax;
    const error = scaleLower === "min" ? scaleMinError : scaleMaxError;
    const label = scaleLower === "min" ? intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MIN_LABEL)
                                       : intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MAX_LABEL);

    const scaleDiv = <div style={{
      width: "48%",
      display: "inline-flex",
      marginLeft: `${scale === "min" ? "0%" : "2%"}`,
      marginRight: `${scale === "max" ? "0%" : "2%"}`
    }}>
        <span style={{
          marginTop: "8px",
          marginRight: "5px",
        }}>{label}</span>
      <FormControl type='number'
                   key={`controlKey_scale_${scaleLower}`}
                   name={`scale_${scaleLower}`}
                   onChange={this.onChange}
                   alt={`${label} Scale`}
                   className="form-control-scale"
                   value={value}
                   min={0}
      />
    </div>;
    if (this.scaleValid(scaleLower)) {
      return scaleDiv;
    } else {
      return <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip_error">{error}</Tooltip>}>
        {scaleDiv}
      </OverlayTrigger>
    }
  }

  private renderScale = () => {
    return <Row key={`key_scale`}>
      <Col componentClass={ControlLabel}
           sm={LABEL_COL}
           className={"ichStyleEditorPopup-Label"}>
        <div>
          <FormattedMessage id="studio.style.ich.scale.label" defaultMessage="Scale Denominator"/>
          <span hidden={this.scalesValid()} style={{color: "red", float: "right"}}>
            <FormattedMessage id="studio.style.ich.error" defaultMessage="*"/>
          </span>
        </div>
      </Col>
      <Col className={"ichStyleEditorPopup-Row"} sm={CONTROL_COL}>
        <div style={{width: "100%"}}>
          {this.renderScaleControl("min")}
          {this.renderScaleControl("max")}
        </div>
      </Col>
      {this.renderHelpCol("Scale", ICH_PAGE_MESSAGES.HELP_SCALE)}
    </Row>

  }

  private renderBands = (provider) => {
    let bandSelect;
    if (provider.requiredNumberOfBands === 3) {
      bandSelect = <div style={{marginBottom: "4px", display: "flow-root"}}>
        {this.renderBandControl("Red")}
        {this.renderBandControl("Green")}
        {this.renderBandControl("Blue")}

        <div style={{float: "right"}}>
          <Button onClick={this.resetBands} style={{padding: "2px 8px", float: "right"}}>
            <FormattedMessage id="studio.form.reset" defaultMessage="Reset"/>
          </Button>
        </div>
      </div>
    } else {
      bandSelect = <div style={{marginBottom: "4px", display: "flow-root"}}>
        <div style={{float: "left", width: "100%"}}>
          <div className='ich_box ich_lightgrey'/>
          &nbsp;{this.getBandControl("Grey", 1, 20)}
          <Button onClick={this.resetBands} style={{padding: "2px 8px", float: "right"}}>
            <FormattedMessage id="studio.form.reset" defaultMessage="Reset"/>
          </Button>
        </div>
      </div>
    }

    return (
        <div key={`key_bandSelect`}>
          <Row className={"ichStyleEditorPopup-Row"}>
            <Col componentClass={ControlLabel}
                 sm={LABEL_COL}
                 className={"ichStyleEditorPopup-Label"}>
              <div>
                <FormattedMessage id="studio.style.ich.bands.label" defaultMessage="Bands"/>
                <span hidden={this.bandsValid()} style={{color: "red", float: "right"}}>
                    <FormattedMessage id="studio.style.ich.error" defaultMessage="*"/>
                </span>
              </div>
            </Col>
            <Col className={"ichStyleEditorPopup-Label"} sm={CONTROL_COL}>
              {bandSelect}
            </Col>
            {this.renderHelpCol("BandSelect", ICH_PAGE_MESSAGES.HELP_BAND_SELECT)}
          </Row>
        </div>
    );
  }

  private resetBands = () => {
    const {selectedIch, changedParameters} = this.state;

    ["Red", "Green", "Blue", "Grey"].forEach((band) => {
      const index = this.getParameterIndexByName(`${selectedIch}_band_${band}`);
      if (index >= 0) {
        changedParameters.splice(index, 1);
      }
    })
    this.setState({changedParameters});
  };

  private renderControl = (parameter, value) => {
    if (value.error === "") {
      return (<div>
        {this.getControl(parameter, value.value)}
      </div>);
    } else {
      return (<div>
        <OverlayTrigger placement="top"
                        overlay={<Tooltip id="tooltip_error">{value.error}</Tooltip>}>
          {this.getControl(parameter, value.value)}
        </OverlayTrigger>
      </div>);
    }
  }

  private getValue = (parameter) => {
    let value = parameter.dataTypes[0].defaultValue.toString();
    let error = "";
    const changedParameter = this.getParameterByName(parameter.name);
    if (changedParameter) {
      value = changedParameter.value.toString();
      error = typeof changedParameter.error === 'undefined' ? "" : changedParameter.error;
    }
    return {value, error};
  }

  private getColorTableFilenameControl = (parameter, value) => {
    const {colorRamps} = this.state;
    const {intl} = this.props;
    const options = [];
    const noFileOption = {
      value: "",
      name: "Color Table Filename",
      label: <div><FormattedMessage id="studio.style.ich.no-file" defaultMessage="No file selected"/></div>
    };
    options.push(noFileOption);
    let selected = options[0];

    colorRamps.forEach((colorramp) => {
      const option = {
        value: colorramp.fileName,
        name: "Color Table Filename",
        label:
            <div>
              <strong><FormattedMessage id="studio.style.ich.ColorTableFilename.name"
                                        defaultMessage="Name:"/></strong> {colorramp.name}<br/>
              <strong><FormattedMessage id="studio.style.ich.ColorTableFilename.description"
                                        defaultMessage="Description:"/></strong> {colorramp.description}<br/>
              <img src={`data:image/png;base64,${colorramp.graphic}`}
                   alt={`${intl.formatMessage(ICH_PAGE_MESSAGES.ALT_COLOR_TABLE)}`}/>
            </div>
      };
      if (colorramp.fileName === value) {
        selected = option;
      }
      options.push(option);
    });

    return <Select
        value={selected}
        onChange={this.onChange}
        options={options}/>
  }

  private getControl = (parameter, value) => {
    if (parameter.name === "Color Table Filename") {
      return this.getColorTableFilenameControl(parameter, value);
    }

    if (parameter.dataTypes[0].name === "IMAGINE.Bool") {
      return <Checkbox inline={true}
                       name={parameter.name}
                       alt={parameter.name}
                       key={`key_${parameter.name}`}
                       checked={value === "true"}
                       onChange={this.onChange}/>;
    }

    let control = <FormControl name={parameter.name}
                               alt={parameter.name}
                               key={`key_${parameter.name}`}
                               value={value}
                               onChange={this.onChange}/>;

    parameter.dataTypes[0].attributes.forEach((attribute) => {
      if (attribute.type === "AcceptableValues") {
        const options: JSX.Element[] = [];
        attribute.value.forEach((optionValue) => {
          const option = <option key={`key_${optionValue}`}
                                 value={(parameter.name === "Stretch Name") ? optionValue.replace(/\s/g, '')
                                                                            : optionValue}>{optionValue}
          </option>;
          options.push(option);
        });
        control = <FormControl name={parameter.name}
                               alt={parameter.name}
                               key={`key_${parameter.name}`}
                               value={value}
                               componentClass="select"
                               onChange={this.onChange}>
          {options}
        </FormControl>
      }
    });
    return control;
  }

  private renderProviders = () => {
    const {selectedIch, ichConfig} = this.state;
    const providers: JSX.Element[] = [];

    ichConfig.imageChainProviders.providers[0].imageChains.forEach((provider) => {
      const option = <option key={`key_${provider.name}`} value={provider.name}>{provider.title}</option>;
      providers.push(option);
    });

    return [<Row key="providerTitle">
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.style.ich.provider.label" defaultMessage="Provider"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="providerName" value={selectedIch} componentClass="select"
                             onChange={this.onChange}>
                  {providers}
                </FormControl>
              </Col>
              {this.renderHelpCol("Providers", ICH_PAGE_MESSAGES.HELP_PROVIDERS)}
            </Row>,
            <Row key="providerDescription">
              <Col sm={LABEL_COL + CONTROL_COL + HELP_COL}>
                <FormattedMessage id={`studio.style.ich.provider-description.${selectedIch}`}
                                  defaultMessage="No description available."/>
              </Col>
            </Row>] as JSX.Element[];
  }

  private renderHelpCol = (id, message) => {
    const {intl} = this.props;
    return <Col sm={HELP_COL}
                className={"ichStyleEditorPopup-Label"}>
      <OverlayTrigger placement="top" overlay={
        <Tooltip id="tooltip_help">
          {intl.formatMessage(message)}
        </Tooltip>
      }>
        <img src={`theme/assets/images/help_16.png`} id={`Providers`}
             alt={intl.formatMessage(ICH_PAGE_MESSAGES.HELP)}/>
      </OverlayTrigger>
    </Col>
  }

  private renderTitleAbstractKeywordsSection = () => {
    const {title, abstract, keywords} = this.state;

    return [<Row key="titleRow">
              <Col componentClass={ControlLabel} sm={LABEL_COL}
                   className={"ichStyleEditorPopup-Label"}>
                <FormattedMessage id="studio.style.ich.title.label" defaultMessage="Title"/>
                <span style={{color: "red", float: "right"}} hidden={title !== ""}>
                <FormattedMessage id="studio.style.ich.title.label.required" defaultMessage="Required"/>
              </span>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="title" value={title} onChange={this.onChange} alt={title}/>
              </Col>
              {this.renderHelpCol("Title", ICH_PAGE_MESSAGES.HELP_TITLE)}
            </Row>,
            <Row key="abstractRow">
              <Col componentClass={ControlLabel} sm={LABEL_COL} className={"ichStyleEditorPopup-Label"}>
                <FormattedMessage id="studio.style.ich.abstract.label" defaultMessage="Abstract"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="abstract" value={abstract} onChange={this.onChange} alt={abstract}/>
              </Col>
              {this.renderHelpCol("Abstract", ICH_PAGE_MESSAGES.HELP_ABSTRACT)}
            </Row>,
            <Row key="keywordsRow">
              <Col componentClass={ControlLabel} sm={LABEL_COL} className={"ichStyleEditorPopup-Label"}>
                <FormattedMessage id="studio.style.ich.keywords.label" defaultMessage="Keywords"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="keywords" value={keywords} onChange={this.onChange} alt={keywords}/>
              </Col>
              {this.renderHelpCol("Keywords", ICH_PAGE_MESSAGES.HELP_KEYWORDS)}
            </Row>] as JSX.Element[];
  }

  private pageError = (): boolean => {
    const {selectedIch, changedParameters} = this.state;
    return !this.scalesValid() ||
           changedParameters.filter(parm => (
               parm.selectedIch === selectedIch && !(parm?.error === "" || typeof parm?.error === "undefined"))
           ).length > 0;
  }

  private renderButtons = () => {
    const {title, errorProvider, errorColorRamp} = this.state;
    const {onClose} = this.props;

    return (
        <div>
          {(!errorProvider && !errorColorRamp) && <span>
            <Button onClick={this.reset}>
              <FormattedMessage id="studio.form.reset" defaultMessage="Reset"/>
            </Button>
            <Button onClick={this.save} disabled={this.pageError() || title === ""}>
              <FormattedMessage id="studio.form.save" defaultMessage="Save"/>
            </Button>
          </span>
          }
          <Button onClick={onClose}><FormattedMessage id="studio.form.close"
                                                      defaultMessage="Close"/></Button>
        </div>
    );
  }

  private validateBandValue = (property) => {
    const {intl} = this.props;
    const value = Number(property.value);
    if (property.name.indexOf('_band_') > 0 && typeof value === "number") {
      if (value <= 0) {
        property.error = intl.formatMessage(ICH_PAGE_MESSAGES.BAND_ERROR);
      }
    }
  }
  private getParameterIndexByName = (parameterName) => {
    const {selectedIch, changedParameters} = this.state;
    return changedParameters.findIndex(parm => (parm.name === parameterName && parm.selectedIch === selectedIch));
  }

  private getParameterByName = (parameterName) => {
    const {selectedIch, changedParameters} = this.state;
    return changedParameters.find(parm => (parm.name === parameterName && parm.selectedIch === selectedIch));
  }

  private updateScaleValue = (name, value) => {
    const {intl} = this.props;
    let {scaleMin, scaleMax} = this.state;
    if (name === "scale_min") {
      scaleMin = value;
    } else {
      scaleMax = value;
    }
    let scaleMinError = "";
    let scaleMaxError = "";
    const scaleMinValue = Number(scaleMin);
    const scaleMaxValue = Number(scaleMax);

    if (scaleMinValue < 0 || scaleMaxValue < 0) {
      if (scaleMinValue < 0) {
        scaleMinError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_ERROR);
      }
      if (scaleMaxValue < 0) {
        scaleMaxError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_ERROR);
      }
      this.setState({scaleMin, scaleMax, scaleMinError, scaleMaxError});
      return;
    }

    if (scaleMin !== "" && scaleMax !== "") {
      if (scaleMinValue > scaleMaxValue) {
        scaleMinError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MIN_OVER_MAX_ERROR);
        scaleMaxError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MAX_OVER_MIN_ERROR);
      }
      if (scaleMinValue === scaleMaxValue) {
        scaleMinError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MIN_EQUAL_MAX_ERROR);
        scaleMaxError = intl.formatMessage(ICH_PAGE_MESSAGES.SCALE_MIN_EQUAL_MAX_ERROR);
      }
    }
    this.setState({scaleMin, scaleMax, scaleMinError, scaleMaxError});
  }

  private onChange = (event: any) => {
    const {ichConfig} = this.state;
    const property = {
      name: event.target ? event.target.name : event.name,
      value: event.target ? event.target.value : event.value,
      error: "",
    }

    if (event.target?.type === "checkbox") {
      property.value = event.target.checked;
    }

    if (property.name.endsWith("_SP")) {  //stretch Property changed
      this.updateStretchParameter(property);
    } else {
      switch (property.name) {
      case "providerName":
        this.setState({selectedIch: property.value});
        break;
      case "title":
        this.setState({title: property.value});
        break;
      case "abstract":
        this.setState({abstract: property.value});
        break;
      case "keywords":
        this.setState({keywords: property.value});
        break;
      case "scale_min":
      case "scale_max":
        this.updateScaleValue(property.name, property.value);
        break;
      default: {
        const {selectedIch} = this.state;
        const provider = ichConfig.imageChainProviders.providers[0].imageChains.find(
            prov => (prov.name === selectedIch));
        if (provider) {
          const parameter = provider.parameters.find(param => (param.name === property.name));
          if (parameter) {
            this.validate(parameter, property);
          } else {
            this.validateBandValue(property);
          }
        }
        //find parameter in changed parameters and update, add if it is missing
        this.updateChangedParameters(property);
      }
      }
    }
  }

  private updateStretchParameter = (property) => {
    const {selectedIch, ichConfig} = this.state;
    const provider = ichConfig.imageChainProviders.providers[0].imageChains.find(prov => (prov.name === selectedIch));
    const stretchParameters = this.getValue(provider.parameters.find(param => (param.name === "Stretch Parameters")));
    const stretchName = this.getValue(provider.parameters.find(param => (param.name === "Stretch Name")));

    if (stretchParameters && stretchName) {
      //get parameter index in string
      const defaultStretchParamIndex = DEFAULT_STRETCH_PARAMETERS.findIndex(
          stretchParamIndex => (stretchParamIndex.name.replace(/\s/g, '') === stretchName.value));
      if (defaultStretchParamIndex) {
        const parameterIndex = DEFAULT_STRETCH_PARAMETERS[defaultStretchParamIndex].parameters.findIndex(
            param => (param.name === property.name.substring(0, property.name.lastIndexOf("_SP"))));
        if (parameterIndex >= 0) {
          //get Stretch Parameters string parts
          const spValue = stretchParameters.value;
          const type = spValue.substring(0, spValue.indexOf(' '));
          const count = spValue.substring(spValue.indexOf('Table (') + 7,
              spValue.indexOf(':') - spValue.indexOf('Table (') + 7);
          const numbers = spValue.substring(spValue.indexOf(':') + 1, spValue.length - 2).replace(/\s/g, '');
          const params = numbers.split(',');
          params[parameterIndex] = property.value;  //update changed value

          //update Stretch Parameter String
          stretchParameters['name'] = "Stretch Parameters";
          stretchParameters.value = `${type} (Table (${count}: ${params.join(',')}))`;
          this.updateChangedParameters(stretchParameters);
        }
      }
    }
  }

  private updateBandErrors = (changedBand) => {
    const {selectedIch, changedParameters} = this.state;
    const {intl} = this.props;

    if (!changedBand.name.endsWith("_band_Grey") && changedBand.name.indexOf("_band_") > 0) {
      const bands = changedParameters.filter(
          param => (param.selectedIch === selectedIch &&
                    !param.name.endsWith("_band_Grey") &&
                    param.name.indexOf('_band_') > 0));
      if (bands.filter(param => (param.value === "")).length === 3 ||
          bands.filter(param => (param.value !== "")).length === 3) {
        bands.forEach((band) => {
          band.error = ""
        });
      } else {
        bands.forEach((band) => {
          band.error = intl.formatMessage(ICH_PAGE_MESSAGES.BAND_ERROR);
        });
      }
    }
  }

  private addInitialRGBValues = (property) => {
    const {selectedIch, changedParameters} = this.state;
    const {intl} = this.props;

    if (!property.name.endsWith("_band_Grey") && property.name.indexOf("_band_") > 0) {
      ["Red", "Green", "Blue"].forEach((band) => {
        if (property.name !== `${selectedIch}_band_${band}`) {
          const newParameter: IchParameter = {
            name: `${selectedIch}_band_${band}`,
            selectedIch,
            value: "",
            error: intl.formatMessage(ICH_PAGE_MESSAGES.BAND_ERROR)
          };
          changedParameters.push(newParameter);
        }
      });
    }
  }

  private setStretchDefault = (property) => {
    const {selectedIch, changedParameters} = this.state;

    if (property.name === "Stretch Name") {
      const defaultStretchParam = DEFAULT_STRETCH_PARAMETERS.findIndex(
          stretchParamIndex => (stretchParamIndex.name === property.value));
      if (defaultStretchParam >= 0) {
        const defaultStretch: IchParameter = {
          name: "Stretch Parameters",
          selectedIch,
          value: DEFAULT_STRETCH_PARAMETERS[defaultStretchParam].defaultValue,
          error: ""
        };
        const defaultStretchIndex = this.getParameterIndexByName("Stretch Parameters");

        if (defaultStretchIndex >= 0) {
          changedParameters[defaultStretchIndex] = defaultStretch;
        } else {
          changedParameters.push(defaultStretch);
        }
      }
    }
  }

  private updateChangedParameters = (property) => {
    const {selectedIch, changedParameters} = this.state;
    const index = this.getParameterIndexByName(property.name);

    if (index >= 0) {  //existing parameter changed
      changedParameters[index] = {...property, selectedIch};
      this.updateBandErrors(property);
    } else { //new parameter changed
      changedParameters.push({...property, selectedIch});
      this.addInitialRGBValues(property);
    }
    this.setStretchDefault(property);
    this.setState({changedParameters});
  }

  private validate = (parameter, property) => {
    switch (parameter.dataTypes[0].name) {
    case "IMAGINE.Int":
    case "IMAGINE.Double":
    case "IMAGINE.Unsigned":
      this.validateNumber(parameter, property);
      break;
    case "IMAGINE.File":
      this.validateFile(parameter, property);
      break;
    }
  }

  private isValidNumberString = (parameter, property): boolean => {
    const {intl} = this.props;

    if (!property.value.match(/^-?\d+\.?(\d+)?$/)) {
      property.error = intl.formatMessage(ICH_PAGE_MESSAGES.NAN);
      return false;
    }
    if (parameter.dataTypes[0].name === "IMAGINE.Unsigned") {
      if (property.value.startsWith("-")) {
        property.error = intl.formatMessage(ICH_PAGE_MESSAGES.UNSIGNED);
        return false;
      }
    }
    return true;
  }

  private numberStringToNumber = (parameter, property): number => {
    let newNumber;
    if (parameter.dataTypes[0].name === "IMAGINE.Int") {
      newNumber = parseInt(property.value, 10);
    } else {
      newNumber = parseFloat(property.value);
      if (parameter.dataTypes[0].name === "IMAGINE.Unsigned") {
        newNumber = Math.abs(newNumber);
      }
    }
    return newNumber;
  }

  private validateNumber = (parameter, property) => {
    const {intl} = this.props;

    if (this.isValidNumberString(parameter, property)) {
      const newNumber = this.numberStringToNumber(parameter, property);

      if (isNaN(newNumber)) {
        property.error = intl.formatMessage(ICH_PAGE_MESSAGES.NAN);
      } else {
        const maxParam = parameter.dataTypes[0].attributes.find((param) => (param.type.toLowerCase() === "max"));
        if (maxParam && newNumber > maxParam.value) {
          property.error = `${intl.formatMessage(ICH_PAGE_MESSAGES.MAX)} ${maxParam.value}`;
        }

        const minParam = parameter.dataTypes[0].attributes.find((param) => (param.type.toLowerCase() === "min"));
        if (minParam && newNumber < minParam.value) {
          property.error = `${intl.formatMessage(ICH_PAGE_MESSAGES.MIN)} ${minParam.value}`;
        }
      }
    }
  }

  private validateFile = (parameter, property) => {
    const {intl} = this.props;
    if (property.value !== "") {
      const attribute = parameter.dataTypes[0].attributes.find(attrib => (attrib.type === "Extension"));
      if (attribute) {
        const extension = attribute.value.substring(attribute.value.lastIndexOf('.')).toUpperCase();
        if (!property.value.toUpperCase().endsWith(extension)) {
          property.error = `${attribute.value} ${intl.formatMessage(ICH_PAGE_MESSAGES.FILE_EXPECTED)}`;
        }
      }
    }
  }

  private closeConfirmationDialog = () => {
    this.props.onClose();
  }

  private addBandString = (bands, vendorOptions) => {
    if ((typeof (bands["Red"]) !== 'undefined') && (typeof (bands["Green"]) !== 'undefined') &&
        (typeof (bands["Blue"]) !== 'undefined')) {
      vendorOptions.push(
          `<VendorOption name="Bands">${bands["Red"]},${bands["Green"]},${bands["Blue"]}</VendorOption>`);
    } else if (typeof (bands["Grey"]) !== 'undefined') {
      vendorOptions.push(
          `<VendorOption name="Bands">${bands["Grey"]},${bands["Grey"]},${bands["Grey"]}</VendorOption>`);
    }
  }

  private getScaleDenominator = (scale, value) => {
    return value === "" ? "" : `<${scale}ScaleDenominator>${value}</${scale}ScaleDenominator>`;
  }

  private save = () => {
    const {selectedIch, changedParameters, title, abstract, keywords, ichConfig, scaleMin, scaleMax} = this.state;
    const vendorOptions = [];
    const bands = {};
    const minScaleDenominator = this.getScaleDenominator('Min', scaleMin);
    const maxScaleDenominator = this.getScaleDenominator('Max', scaleMax);

    vendorOptions.push(`<VendorOption name="ImageChainName">${selectedIch}</VendorOption>`);
    changedParameters.forEach((parameter) => {
      if (parameter.selectedIch === selectedIch) {
        if (parameter.name.indexOf("_band_") > 0) {
          bands[parameter.name.substring(parameter.name.lastIndexOf('_') + 1)] = parameter.value;
        } else {
          const provider = ichConfig.imageChainProviders.providers[0].imageChains.find(
              prov => (prov.name === selectedIch));
          const providerParameter = provider.parameters.find(param => (param.name === parameter.name));
          const defaultValue = (providerParameter) ? providerParameter.dataTypes[0].defaultValue : "";
          if (parameter.value !== defaultValue) {  //Only Save changed/Not-Default values to SLD
            vendorOptions.push(`<VendorOption name="${parameter.name}">${parameter.value.toString()}</VendorOption>`);
          }
        }
      }
    });
    this.addBandString(bands, vendorOptions);

    const sld = `<?xml version='1.0' encoding='UTF-8'?>
<FeatureTypeStyle xmlns="http://www.opengis.net/se" version="1.1.0">
<Name>${selectedIch}</Name>
<Description>
<Title>${title}</Title>
<Abstract>${abstract}</Abstract>
</Description>
<Rule>
<Name>rasters</Name>
${minScaleDenominator}
${maxScaleDenominator}
<RasterSymbolizer>
${vendorOptions.join('\n')}
</RasterSymbolizer>
</Rule>
</FeatureTypeStyle>`;

    const keywordsArray = ["Image Chain"];
    keywords.split(',').forEach(keyword => {
      keywordsArray.push(keyword.trim());
    });
    ApolloApi.saveSLDStyle(title, sld, keywordsArray).then(rAdd => {
      this.setState({confirmationMessage: rAdd[0].message, showConfirmationDialog: true})
    });
  };

  private reset = () => {
    this.setState({changedParameters: []});
  };

  private onLoad = () => {
    const {sld, keywords} = this.props;

    if (sld) {
      const parser = new DOMParser();
      const xml = parser.parseFromString(sld, 'text/xml');
      const imageChainName = xml.querySelector('VendorOption[name="ImageChainName"]');
      if (imageChainName) {
        const selectedIch = imageChainName.innerHTML;
        const title = this.getXmlValue(xml, "Title");
        const abstract = this.getXmlValue(xml, "Abstract");
        const changedParameters: IchParameter[] = [];
        const vendorOptions = xml.querySelectorAll('VendorOption');
        if (vendorOptions) {
          vendorOptions.forEach((vendorOption) => {
            if (vendorOption.getAttribute("name") !== "ImageChainName") {
              changedParameters.push({
                selectedIch,
                name: vendorOption.getAttribute("name"),
                value: vendorOption.innerHTML
              });
            }
          });
        }
        this.setState({
          title,
          abstract,
          selectedIch,
          changedParameters,
          keywords: typeof keywords === 'undefined' ? "" : keywords,
        });
      }
    }
  }

  private getXmlValue = (xml, value): string => {
    return xml.querySelector(value) ? xml.querySelector(value).innerHTML : "";
  }
}

export const IchStyleEditorPopup = injectIntl(IchStyleEditorPopupComponent);